import type { ReactNode } from "react";
import type { AiInstructionPart } from "../../api/types";

/** Text that runs over this many characters starts folded (the blog writer's skill files). */
const FOLD_OVER = 1200;

/** One read-only piece of a system's instructions: locked (grey, monospace) or editable. */
export function AiInstructionPartView({ part, startOpen }: { part: AiInstructionPart; startOpen: boolean }) {
  const head = (
    <AiPartHead part={part}>
      {part.uploaded === true ? <span className="ai-control-pill is-live">Uploaded</span> : null}
      {part.uploaded === false ? <span className="ai-control-pill is-warning">Not uploaded</span> : null}
    </AiPartHead>
  );
  const body = <div className={`ai-control-text${part.locked ? " is-locked" : ""}`}>{part.text}</div>;
  const className = `ai-control-part${part.locked ? " is-locked" : ""}`;
  if (part.text.length > FOLD_OVER) {
    return (
      <details className={className} open={startOpen}>
        <summary>{head}</summary>
        {body}
      </details>
    );
  }
  return (
    <div className={className}>
      {head}
      {body}
    </div>
  );
}

/** "LOCKED · title · Used by the code" or "EDITABLE · title", then whatever pills the caller adds. */
export function AiPartHead({
  part,
  labelFor,
  children,
}: {
  part: AiInstructionPart;
  /** The editable label belongs to a textarea: name it. */
  labelFor?: string;
  children?: ReactNode;
}) {
  const label = part.locked ? "Locked" : "Editable";
  return (
    <span className="ai-control-part-head">
      {part.locked ? <LockIcon /> : null}
      {labelFor ? (
        <label className="ai-control-part-label" htmlFor={labelFor}>
          {label}
          {part.title ? <span className="ai-control-part-title">{part.title}</span> : null}
        </label>
      ) : (
        <>
          <span className="ai-control-part-label">{label}</span>
          {part.title ? <span className="ai-control-part-title">{part.title}</span> : null}
        </>
      )}
      {part.locked ? <span className="ai-control-pill is-muted">Used by the code</span> : null}
      {children}
    </span>
  );
}

function LockIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
